import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { CollectionManager } from '../components/CollectionManager';
import { ShareButtons } from '../components/ShareButtons';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  FolderOpen, 
  Folder,
  Plus,
  Trash2,
  Edit2,
  ArrowLeft,
  Clock,
  MoreVertical,
  X,
  Save
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CollectionsPage() {
  const navigate = useNavigate();
  const { collectionId } = useParams();
  const { isAuthenticated, token, loading: authLoading } = useAuth();
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingCollection, setEditingCollection] = useState(null);
  const [editName, setEditName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }

    if (isAuthenticated) {
      fetchCollections();
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (collectionId && isAuthenticated) {
      fetchCollectionDetails(collectionId);
    } else {
      setSelectedCollection(null);
    }
  }, [collectionId, isAuthenticated]);

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/collections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCollections(response.data);
    } catch (error) {
      console.error('Failed to fetch collections:', error);
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const fetchCollectionDetails = async (id) => {
    try {
      const response = await axios.get(`${API_URL}/api/collections/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedCollection(response.data);
    } catch (error) {
      console.error('Failed to fetch collection:', error);
      if (error.response?.status === 404) {
        toast.error('Collection not found');
        navigate('/collections');
      }
    }
  };

  const handleCollectionCreated = (newCollection) => {
    setCollections([newCollection, ...collections]);
  };

  const handleUpdateCollection = async () => {
    if (!editName.trim() || !editingCollection) return;

    try {
      await axios.put(
        `${API_URL}/api/collections/${editingCollection.id}`,
        { name: editName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCollections(collections.map(c => 
        c.id === editingCollection.id ? { ...c, name: editName.trim() } : c
      ));
      
      if (selectedCollection?.id === editingCollection.id) {
        setSelectedCollection({ ...selectedCollection, name: editName.trim() });
      }
      
      toast.success('Collection updated!');
      setEditingCollection(null);
    } catch (error) {
      console.error('Failed to update collection:', error);
      toast.error('Failed to update collection');
    }
  };

  const handleDeleteCollection = async () => {
    if (!collectionToDelete) return;

    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/collections/${collectionToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setCollections(collections.filter(c => c.id !== collectionToDelete.id));
      
      if (selectedCollection?.id === collectionToDelete.id) {
        navigate('/collections');
      }
      
      toast.success('Collection deleted');
      setDeleteDialogOpen(false);
      setCollectionToDelete(null);
    } catch (error) {
      console.error('Failed to delete collection:', error);
      toast.error('Failed to delete collection');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRemoveFontFromCollection = async (fontId) => {
    if (!selectedCollection) return;

    try {
      await axios.delete(
        `${API_URL}/api/collections/${selectedCollection.id}/fonts/${fontId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSelectedCollection({
        ...selectedCollection,
        fonts: selectedCollection.fonts.filter(f => f.id !== fontId),
        font_ids: selectedCollection.font_ids.filter(id => id !== fontId)
      });
      
      toast.success('Font removed from collection');
    } catch (error) {
      console.error('Failed to remove font:', error);
      toast.error('Failed to remove font');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0D0507]">
        <Header />
        <main className="pt-24 pb-16 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Gothic Collections Skeleton */}
            <div className="animate-gothic-fade space-y-8">
              {/* Header */}
              <div className="flex justify-between items-start">
                <div className="space-y-4">
                  <div className="skeleton-text w-20 h-4" />
                  <div className="skeleton-text w-48 h-12" />
                  <div className="skeleton-text w-60 h-4" />
                </div>
                <div className="skeleton-button w-36 h-11" />
              </div>
              
              {/* Collections grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-cathedral">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="skeleton-card p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="skeleton-avatar w-10 h-10" />
                      <div className="space-y-2">
                        <div className="skeleton-text w-28 h-4" />
                        <div className="skeleton-text w-16 h-3" />
                      </div>
                    </div>
                    <div className="skeleton-text w-36 h-3" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Collection detail view
  if (selectedCollection) {
    return (
      <div className="min-h-screen bg-[#0D0507]" data-testid="collection-detail-page">
        <Header />
        
        <main className="pt-24 pb-16 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Back Button */}
            <button
              onClick={() => navigate('/collections')}
              className="flex items-center gap-2 text-[#C2B49A] hover:text-[#D4AF37] mb-8 transition-colors"
              data-testid="back-to-collections"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-cinzel uppercase tracking-wider">All Collections</span>
            </button>

            {/* Collection Header */}
            <div className="mb-12 flex items-start justify-between">
              <div>
                <span className="mono-label">Collection</span>
                {editingCollection?.id === selectedCollection.id ? (
                  <div className="flex items-center gap-2 mt-4">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="input-terminal text-4xl font-cinzel font-bold rounded-lg h-14"
                      data-testid="edit-collection-input"
                    />
                    <Button
                      onClick={handleUpdateCollection}
                      className="btn-primary rounded-lg h-14"
                    >
                      <Save className="w-5 h-5" />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setEditingCollection(null)}
                      className="btn-secondary rounded-lg h-14"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                ) : (
                  <h1 
                    className="text-4xl lg:text-5xl font-cinzel font-bold tracking-wide mt-4 text-[#F4EFE6]"
                    data-testid="collection-name"
                  >
                    {selectedCollection.name}
                  </h1>
                )}
                <p className="text-[#C2B49A] mt-2 font-serif">
                  {selectedCollection.fonts?.length || 0} fonts · Created {formatDate(selectedCollection.created_at)}
                </p>
              </div>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="btn-secondary rounded-lg">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg">
                  <DropdownMenuItem
                    onClick={() => {
                      setEditingCollection(selectedCollection);
                      setEditName(selectedCollection.name);
                    }}
                    className="text-[#C2B49A] hover:text-[#F4EFE6] focus:text-[#F4EFE6] focus:bg-[#220F13] rounded-lg font-serif"
                  >
                    <Edit2 className="w-4 h-4 mr-2 text-[#D4AF37]" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-[#9E2A2B] hover:text-[#FF6B6B] focus:text-[#FF6B6B] focus:bg-[#9E2A2B]/10 rounded-lg font-serif"
                    onClick={() => {
                      setCollectionToDelete(selectedCollection);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Fonts Grid */}
            {selectedCollection.fonts?.length === 0 ? (
              <div className="card-surface p-12 text-center rounded-lg">
                <FolderOpen className="w-16 h-16 text-[#C2B49A] mx-auto mb-4" />
                <h3 className="text-2xl font-cinzel mb-2 text-[#F4EFE6]">
                  No Fonts Yet
                </h3>
                <p className="text-[#C2B49A] mb-6 font-serif">
                  Add fonts to this collection from the generator or dashboard
                </p>
                <Button
                  onClick={() => navigate('/generator')}
                  className="btn-primary rounded-lg"
                >
                  Create a Font
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {selectedCollection.fonts.map((font) => (
                  <div 
                    key={font.id}
                    className="card-surface p-6 rounded-lg"
                    data-testid={`collection-font-${font.id}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="mono-label">{font.name}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveFontFromCollection(font.id)}
                        className="text-[#C2B49A] hover:text-[#9E2A2B] h-8 w-8 p-0"
                        data-testid={`remove-font-${font.id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div 
                      className="min-h-[80px] flex items-center mb-4 cursor-pointer"
                      onClick={() => navigate(`/font/${font.id}/preview`)}
                    >
                      <p 
                        className="text-2xl text-[#F4EFE6]"
                        style={{
                          fontFamily: font.css_properties?.fontFamily || 'Inter',
                          fontWeight: font.style_properties?.weight || 400,
                          fontStyle: font.style_properties?.style || 'normal',
                        }}
                      >
                        {font.preview_text}
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-[#D4AF37]/20">
                      <span className="text-xs text-[#C2B49A] flex items-center gap-1 font-serif">
                        <Clock className="w-3 h-3" />
                        {formatDate(font.created_at)}
                      </span>
                      <ShareButtons font={font} className="scale-90" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg">
            <DialogHeader>
              <DialogTitle className="font-cinzel text-[#F4EFE6]">
                Delete Collection
              </DialogTitle>
              <DialogDescription className="text-[#C2B49A] font-serif">
                Are you sure you want to delete "{collectionToDelete?.name}"? This won&apos;t delete the fonts themselves.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                className="btn-secondary rounded-lg"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteCollection}
                disabled={isDeleting}
                className="bg-[#9E2A2B] hover:bg-[#7A2022] text-white rounded-lg"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Collections list view
  return (
    <div className="min-h-screen bg-[#0D0507]" data-testid="collections-page">
      <Header />
      
      <main className="pt-24 pb-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-12 flex items-start justify-between">
            <div>
              <span className="mono-label">Organize</span>
              <h1 className="text-4xl lg:text-5xl font-cinzel font-bold tracking-wide mt-4 text-[#F4EFE6]">
                Collections
              </h1>
              <p className="text-[#C2B49A] mt-2 font-serif">
                Organize your fonts into collections
              </p>
            </div>
            <CollectionManager 
              collections={collections}
              onCollectionCreated={handleCollectionCreated}
            />
          </div>

          {/* Collections Grid */}
          {collections.length === 0 ? (
            <div className="card-surface p-12 text-center rounded-lg">
              <FolderOpen className="w-16 h-16 text-[#C2B49A] mx-auto mb-4" />
              <h3 className="text-2xl font-cinzel mb-2 text-[#F4EFE6]">
                No Collections Yet
              </h3>
              <p className="text-[#C2B49A] mb-6 font-serif">
                Create your first collection to organize your fonts
              </p>
              <CollectionManager 
                collections={collections}
                onCollectionCreated={handleCollectionCreated}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {collections.map((collection) => (
                <div 
                  key={collection.id}
                  className="card-surface p-6 rounded-lg cursor-pointer hover:border-[#D4AF37] transition-colors group"
                  onClick={() => navigate(`/collections/${collection.id}`)}
                  data-testid={`collection-card-${collection.id}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#722F37] flex items-center justify-center rounded-lg group-hover:bg-[#5A252C] transition-colors">
                        <Folder className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <h3 className="font-cinzel font-medium text-[#F4EFE6]">
                          {collection.name}
                        </h3>
                        <p className="text-xs text-[#C2B49A] font-serif">
                          {collection.font_count || 0} fonts
                        </p>
                      </div>
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button 
                          variant="ghost" 
                          className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-[#C2B49A] hover:text-[#D4AF37]"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingCollection(collection);
                            setEditName(collection.name);
                          }}
                          className="text-[#C2B49A] hover:text-[#F4EFE6] focus:text-[#F4EFE6] focus:bg-[#220F13] rounded-lg font-serif"
                        >
                          <Edit2 className="w-4 h-4 mr-2 text-[#D4AF37]" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-[#9E2A2B] hover:text-[#FF6B6B] focus:text-[#FF6B6B] focus:bg-[#9E2A2B]/10 rounded-lg font-serif"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCollectionToDelete(collection);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <div className="text-xs text-[#C2B49A] flex items-center gap-1 font-serif">
                    <Clock className="w-3 h-3" />
                    Created {formatDate(collection.created_at)}
                  </div>
                </div>
              ))}
              
              {/* Add New Collection Card */}
              <div 
                className="card-surface p-6 border-dashed cursor-pointer hover:border-[#D4AF37] transition-colors flex items-center justify-center min-h-[120px] rounded-lg"
                onClick={() => document.querySelector('[data-testid="create-collection-btn"]')?.click()}
              >
                <div className="text-center">
                  <Plus className="w-8 h-8 text-[#C2B49A] mx-auto mb-2" />
                  <p className="text-sm text-[#C2B49A] font-serif">New Collection</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Rename Dialog */}
      <Dialog open={!!editingCollection && editingCollection.id !== selectedCollection?.id} onOpenChange={() => setEditingCollection(null)}>
        <DialogContent className="sm:max-w-md bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-[#F4EFE6]">
              Rename Collection
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleUpdateCollection()}
              className="input-terminal rounded-lg"
              placeholder="Collection name..."
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingCollection(null)}
              className="btn-secondary rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateCollection}
              className="btn-primary rounded-lg"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-[#F4EFE6]">
              Delete Collection
            </DialogTitle>
            <DialogDescription className="text-[#C2B49A] font-serif">
              Are you sure you want to delete "{collectionToDelete?.name}"? This won&apos;t delete the fonts themselves.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="btn-secondary rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteCollection}
              disabled={isDeleting}
              className="bg-[#9E2A2B] hover:bg-[#7A2022] text-white rounded-lg"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
